import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link,
  useToast,
  Icon,
  Flex,
  Image,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { FaKey } from "react-icons/fa";
import axios from "axios";
import { FORGOT_PASSWORD_URL } from "../services/api";
import { useNavigate } from "react-router-dom";

const ForgotPasswordPage = () => {
  const [form, setForm] = useState({ companyName: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const bgMain = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textPrimary = useColorModeValue("gray.700", "gray.100");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.companyName || !form.email) {
      return toast({
        title: "Missing Fields",
        description: "Company name and email are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);

    try {
      const response = await axios.post(FORGOT_PASSWORD_URL, {
        tenantSlug: form.tenantSlug,
        email: form.email,
      });

      toast({
        title: "Reset Link Sent",
        description:
          response.data.message || "Check your email for reset instructions.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // In development, show the token
      if (response.data.resetToken) {
        console.log("Reset Token (DEV ONLY):", response.data.resetToken);
        toast({
          title: "Development Token",
          description: `Token: ${response.data.resetToken}`,
          status: "info",
          duration: 10000,
          isClosable: true,
        });
      }

      // Redirect to login after short delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to send reset link.";
      toast({
        title: "Request Failed",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg={bgMain} p={4}>
      <Box
        width="100%"
        maxW="480px"
        boxShadow="lg"
        rounded="xl"
        overflow="hidden"
        bg={cardBg}
        p={8}
      >
        <VStack spacing={6}>
          <Icon as={FaKey} boxSize={10} color="teal.400" />
          <Heading size="lg" color={textPrimary} textAlign="center">
            Forgot Password?
          </Heading>
          <Text fontSize="sm" color={textSecondary} textAlign="center">
            Enter your company name and email address. We'll send you
            instructions to reset your password.
          </Text>

          <Stack spacing={4} width="100%">
            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textPrimary}>
                Company Name
              </FormLabel>
              <Input
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                placeholder="e.g. Kaan Corporation"
                autoComplete="organization"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textPrimary}>
                Email Address
              </FormLabel>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                autoComplete="email"
              />
            </FormControl>

            <Button
              colorScheme="teal"
              size="lg"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Sending..."
            >
              Send Reset Link
            </Button>

            <Text fontSize="sm" textAlign="center" color={textPrimary}>
              Remember your password?{" "}
              <Link
                color="teal.400"
                fontWeight="medium"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Link>
            </Text>
          </Stack>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ForgotPasswordPage;
